import { FormattedMessage } from 'react-intl';
import componentMessages from './messages';
import generalMessages from '../App/messages';

const messages = { ...generalMessages, ...componentMessages };

export const COMPLIANCE_TYPES: Object = {
  ALL_OPEN_CERTS: 'allOpenCerts',
  NEEDS_REVIEW: 'needsReview',
};
export const HEADERS = [
  {
    id: 'certificationType',
    label: <FormattedMessage {...messages.certificationType} />,
    dataType: 'string',
    sortable: true,
    multiselect: true,
    searchable: true,
  },
  {
    id: 'certStatus',
    label: <FormattedMessage {...messages.certStatus} />,
    dataType: 'string',
    sortable: true,
    multiselect: true,
    searchable: true,
  },
  {
    id: 'propertyName',
    label: <FormattedMessage {...messages.propertyName} />,
    dataType: 'string',
    sortable: true,
    multiselect: true,
    searchable: true,
  },
  {
    id: 'unitNumber',
    label: <FormattedMessage {...messages.unitNumber} />,
    dataType: 'string',
    sortable: true,
    searchable: true,
  },
  {
    id: 'applicantName',
    label: <FormattedMessage {...messages.applicantName} />,
    dataType: 'string',
    sortable: true,
    searchable: true,
  },
  {
    id: 'complianceDocumentType',
    label: <FormattedMessage {...messages.complianceDocumentType} />,
    dataType: 'string',
    sortable: true,
    multiselect: true,
    searchable: true,
  },
  {
    id: 'uploadDate',
    label: <FormattedMessage {...messages.uploadDateTime} />,
    dataType: 'date',
    sortable: true,
    searchable: false,
  },

  {
    id: 'isReviewed',
    label: <FormattedMessage {...messages.isReviewed} />,
    dataType: 'string',
    sortable: true,
  },
  {
    id: 'programType',
    label: <FormattedMessage {...messages.programType} />,
    dataType: 'string',
    multiselect: true,
    sortable: true,
  },
  {
    id: 'voucherEffectiveDate',
    label: <FormattedMessage {...messages.voucherEffectiveDate} />,
    dataType: 'date',
    sortable: true,
    searchable: false,
  },
  {
    id: 'certHistory',
    label: <FormattedMessage {...messages.certHistory} />,
    dataType: 'string',
    sortable: true,
  },
  {
    id: 'complianceApprovalStatus',
    label: <FormattedMessage {...messages.complianceApprovalStatus} />,
    dataType: 'string',
    multiselect: true,
    sortable: true,
  },
  {
    id: 'isActive',
    label: <FormattedMessage {...messages.isActive} />,
    dataType: 'boolean',
    filterOptions: [
      { value: 'yes', text: <FormattedMessage {...messages.yes} /> },
      { value: 'no', text: <FormattedMessage {...messages.no} /> },
    ],
    multiselect: true,
    sortable: true,
  },
  {
    id: 'isResident',
    label: <FormattedMessage {...messages.isResident} />,
    dataType: 'boolean',
    filterOptions: [
      { value: 'yes', text: <FormattedMessage {...messages.yes} /> },
      { value: 'no', text: <FormattedMessage {...messages.no} /> },
    ],
    multiselect: true,
    sortable: true,
  },
  {
    id: 'isPastDue',
    label: <FormattedMessage {...messages.pastDue} />,
    dataType: 'boolean',
    filterOptions: [
      { value: 'yes', text: <FormattedMessage {...messages.yes} /> },
      { value: 'no', text: <FormattedMessage {...messages.no} /> },
    ],
    multiselect: true,
    sortable: true,
  },
  {
    id: 'isReviewOverdue',
    label: <FormattedMessage {...messages.reviewOverdue} />,
    dataType: 'boolean',
    filterOptions: [
      { value: 'yes', text: <FormattedMessage {...messages.yes} /> },
      { value: 'no', text: <FormattedMessage {...messages.no} /> },
    ],
    multiselect: true,
    sortable: true,
  },
  {
    id: 'isAttentionRequired',
    label: <FormattedMessage {...messages.attentionRequired} />,
    dataType: 'boolean',
    filterOptions: [
      { value: 'yes', text: <FormattedMessage {...messages.yes} /> },
      { value: 'no', text: <FormattedMessage {...messages.no} /> },
    ],
    multiselect: true,
    sortable: true,
  },
];

// Planning-range quick filter (ENG-30055 Phase 3a). Same terminology/value
// convention as ManageCertifications/constantsV2.js's RECERT_DUE_FILTER_VALUES
// so both screens speak the same filter-value vocabulary.
export const RECERT_RANGE_FILTER_VALUES = {
  LATE: 'late',
  UNDER_30: 'under30',
  BETWEEN_31_AND_60: 'between31and60',
  BETWEEN_61_AND_90: 'between61and90',
  BETWEEN_90_AND_120: 'between90and120',
  UPCOMING: 'upcoming',
};

// Maps the row's backend-computed recertRange value (see
// hooks.useParseResults.js, ultimately sourced from fortress-backend's
// getRecertRange) to the frontend filter-value ids above. Mirrors
// ManageCertifications/constantsV2.js's RECERT_RANGE_TO_FILTER_MAP exactly.
export const RECERT_RANGE_TO_FILTER_MAP = {
  lateRecerts: RECERT_RANGE_FILTER_VALUES.LATE,
  under30: RECERT_RANGE_FILTER_VALUES.UNDER_30,
  between31And60: RECERT_RANGE_FILTER_VALUES.BETWEEN_31_AND_60,
  between61And90: RECERT_RANGE_FILTER_VALUES.BETWEEN_61_AND_90,
  between90And120: RECERT_RANGE_FILTER_VALUES.BETWEEN_90_AND_120,
  upcoming: RECERT_RANGE_FILTER_VALUES.UPCOMING,
};

/**
 * OR-across-selected-buckets membership test for the portfolio "planning
 * range" quick filter. An empty `filterBy` means the filter isn't active (show
 * everything). Rows with no `recertRange` (null/undefined — non-Recert cert
 * types) never match a non-empty selection.
 */
export const recertRangeFilterFn = (
  filterBy: string[] = [],
  recertRangeValue: ?string,
): boolean => {
  if (!filterBy || filterBy.length === 0) {
    return true;
  }

  if (recertRangeValue === null || recertRangeValue === undefined) {
    return false;
  }

  const mappedValue = RECERT_RANGE_TO_FILTER_MAP[recertRangeValue] ?? null;
  return mappedValue ? filterBy.includes(mappedValue) : false;
};

export const PROPERTY_PATH_MAP = (includeNotStartedCerts: boolean) => ({
  certificationType: {
    path: 'certificationType',
    dataType: 'string',
  },
  ...(includeNotStartedCerts
    ? {
        certStatus: {
          path: 'certStatus',
          dataType: 'string',
        },
      }
    : []),
  propertyName: {
    path: 'propertyName',
    dataType: 'string',
  },
  unitNumber: {
    path: 'unitNumber',
    dataType: 'string',
  },
  applicantName: {
    path: 'applicantName',
    dataType: 'string',
  },
  complianceDocumentType: {
    path: 'complianceDocumentType',
    dataType: 'string',
  },
  uploadDate: {
    path: 'uploadDate',
    dataType: 'date',
  },
  isReviewed: {
    path: 'isReviewed',
    dataType: 'string',
  },
  programType: {
    path: 'programType',
    dataType: 'string',
  },
  voucherEffectiveDate: {
    path: 'voucherEffectiveDate',
    dataType: 'date',
  },
  certHistory: {
    path: 'certHistory',
    dataType: 'string',
  },
  complianceApprovalStatus: {
    path: 'complianceApprovalStatus',
    dataType: 'string',
  },
  isActive: {
    path: 'isActive',
    dataType: 'boolean',
  },
  isResident: {
    path: 'isResident',
    dataType: 'boolean',
  },
  isPastDue: {
    path: 'isPastDue',
    dataType: 'boolean',
  },
  isReviewOverdue: {
    path: 'isReviewOverdue',
    dataType: 'boolean',
  },
  isAttentionRequired: {
    path: 'isAttentionRequired',
    dataType: 'boolean',
  },
  recertRange: {
    path: 'recertRange',
    dataType: 'string',
    filterFn: recertRangeFilterFn,
  },
});
